/**
 * Table Filter Storage Utility
 *
 * Gestisce la persistenza dei filtri delle tabelle con supporto per:
 * - Session storage (default): filtri persi alla chiusura del browser
 * - Persistent storage (localStorage): filtri con TTL configurabile
 * - Eviction automatica (max 50 entries)
 * - Validazione filtri contro field keys attuali
 */

const STORAGE_PREFIX = 'table_filters_'
const MAX_ENTRIES = 50
const DEFAULT_TTL_HOURS = 48
const STORAGE_VERSION = 1

/**
 * Genera la chiave di storage
 * @param {string} tableName - Nome della tabella
 * @param {string} contextKey - Chiave contesto opzionale
 * @returns {string}
 */
function getStorageKey(tableName, contextKey) {
  return `${STORAGE_PREFIX}${tableName}_${contextKey || 'default'}`
}

/**
 * Ottiene lo storage appropriato in base alla modalità
 * @param {string} mode - 'session' | 'persistent'
 * @returns {Storage}
 */
function getStorage(mode) {
  return mode === 'persistent' ? localStorage : sessionStorage
}

/**
 * Controlla se un entry è scaduto
 * @param {object} meta - Metadati dell'entry
 * @param {number} ttlHours - TTL in ore
 * @returns {boolean}
 */
function isExpired(meta, ttlHours) {
  if (!meta || !meta.createdAt) return true
  const now = Date.now()
  const ttlMs = ttlHours * 60 * 60 * 1000
  return now - meta.createdAt > ttlMs
}

/**
 * Rimuove entries scadute e mantiene il limite massimo
 * @param {string} mode - 'session' | 'persistent'
 * @param {number} ttlHours - TTL in ore
 */
function evictOldEntries(mode, ttlHours) {
  const storage = getStorage(mode)
  const entries = []

  // Raccogli tutte le entries del table filter storage
  for (let i = 0; i < storage.length; i++) {
    const key = storage.key(i)
    if (key && key.startsWith(STORAGE_PREFIX)) {
      try {
        const data = JSON.parse(storage.getItem(key))
        if (data && data._meta) {
          entries.push({
            key,
            createdAt: data._meta.createdAt || 0
          })
        }
      } catch (e) {
        // Entry corrotta, rimuovi
        storage.removeItem(key)
      }
    }
  }

  // Rimuovi entries scadute
  entries.forEach(entry => {
    try {
      const data = JSON.parse(storage.getItem(entry.key))
      if (data && data._meta && isExpired(data._meta, ttlHours)) {
        storage.removeItem(entry.key)
      }
    } catch (e) {
      storage.removeItem(entry.key)
    }
  })

  // Se ancora troppe entries, rimuovi le più vecchie
  const currentEntries = []
  for (let i = 0; i < storage.length; i++) {
    const key = storage.key(i)
    if (key && key.startsWith(STORAGE_PREFIX)) {
      try {
        const data = JSON.parse(storage.getItem(key))
        if (data && data._meta) {
          currentEntries.push({
            key,
            createdAt: data._meta.createdAt || 0
          })
        }
      } catch (e) {
        // Skip
      }
    }
  }

  if (currentEntries.length > MAX_ENTRIES) {
    // Ordina per data creazione (più vecchi prima)
    currentEntries.sort((a, b) => a.createdAt - b.createdAt)
    // Rimuovi i più vecchi fino a MAX_ENTRIES
    const toRemove = currentEntries.slice(0, currentEntries.length - MAX_ENTRIES)
    toRemove.forEach(entry => {
      storage.removeItem(entry.key)
    })
  }
}

/**
 * Valida i filtri contro i field keys attuali
 * Rimuove filtri orfani (su campi che non esistono più)
 * @param {object} state - Stato salvato
 * @param {string[]} currentFieldKeys - Chiavi dei campi attuali
 * @returns {object} - Stato validato
 */
function validateFilters(state, currentFieldKeys) {
  if (!state || !currentFieldKeys || currentFieldKeys.length === 0) {
    return state
  }

  const validatedState = { ...state }

  // Valida filters
  if (validatedState.filters) {
    const validFilters = {}
    Object.keys(validatedState.filters).forEach(key => {
      // Controlla se il field esiste o se è un parametro derivato (es. date_from, date_to)
      const baseKey = key.replace(/_from$|_to$/, '')
      if (currentFieldKeys.includes(key) || currentFieldKeys.includes(baseKey)) {
        validFilters[key] = validatedState.filters[key]
      }
    })
    validatedState.filters = validFilters
  }

  // Valida dateRangeFilters
  if (validatedState.dateRangeFilters) {
    const validDateFilters = {}
    Object.keys(validatedState.dateRangeFilters).forEach(key => {
      if (currentFieldKeys.includes(key)) {
        validDateFilters[key] = validatedState.dateRangeFilters[key]
      }
    })
    validatedState.dateRangeFilters = validDateFilters
  }

  // Valida sorting
  if (validatedState.sorting && validatedState.sorting.column) {
    if (!currentFieldKeys.includes(validatedState.sorting.column)) {
      validatedState.sorting = { column: null, asc: false }
    }
  }

  return validatedState
}

/**
 * Salva lo stato della tabella nello storage
 * @param {string} tableName - Nome della tabella
 * @param {string} contextKey - Chiave contesto opzionale
 * @param {object} state - Stato da salvare { filters, dateRangeFilters, sorting, pagination }
 * @param {object} options - Opzioni { mode: 'session'|'persistent', ttlHours: number, fieldKeys: string[] }
 */
export function saveTableState(tableName, contextKey, state, options = {}) {
  if (!tableName) return

  const { mode = 'session', ttlHours = DEFAULT_TTL_HOURS, fieldKeys = [] } = options
  const storage = getStorage(mode)
  const key = getStorageKey(tableName, contextKey)

  const dataToSave = {
    filters: state.filters || {},
    dateRangeFilters: state.dateRangeFilters || {},
    sorting: state.sorting || { column: null, asc: false },
    pagination: state.pagination || { page: 1, page_size: 20 },
    _meta: {
      createdAt: Date.now(),
      version: STORAGE_VERSION,
      fieldKeys: fieldKeys
    }
  }

  try {
    // Eviction prima di salvare
    evictOldEntries(mode, ttlHours)
    storage.setItem(key, JSON.stringify(dataToSave))
  } catch (e) {
    // Storage pieno o altro errore, fail silently
  }
}

/**
 * Carica lo stato della tabella dallo storage
 * @param {string} tableName - Nome della tabella
 * @param {string} contextKey - Chiave contesto opzionale
 * @param {object} options - Opzioni { mode: 'session'|'persistent', ttlHours: number, fieldKeys: string[] }
 * @returns {object|null} - Stato caricato o null se non trovato/scaduto
 */
export function loadTableState(tableName, contextKey, options = {}) {
  if (!tableName) return null

  const { mode = 'session', ttlHours = DEFAULT_TTL_HOURS, fieldKeys = [] } = options
  const storage = getStorage(mode)
  const key = getStorageKey(tableName, contextKey)

  try {
    const raw = storage.getItem(key)
    if (!raw) return null

    const data = JSON.parse(raw)
    if (!data || !data._meta) {
      // Dati corrotti
      storage.removeItem(key)
      return null
    }

    // Controlla TTL per persistent mode
    if (mode === 'persistent' && isExpired(data._meta, ttlHours)) {
      storage.removeItem(key)
      return null
    }

    // Valida contro fieldKeys attuali
    const validatedData = validateFilters(data, fieldKeys)

    return {
      filters: validatedData.filters || {},
      dateRangeFilters: validatedData.dateRangeFilters || {},
      sorting: validatedData.sorting || { column: null, asc: false },
      pagination: validatedData.pagination || { page: 1, page_size: 20 }
    }
  } catch (e) {
    // Dati corrotti, rimuovi
    try {
      storage.removeItem(key)
    } catch (removeError) {
      // Ignore
    }
    return null
  }
}

/**
 * Cancella lo stato della tabella dallo storage
 * @param {string} tableName - Nome della tabella
 * @param {string} contextKey - Chiave contesto opzionale
 * @param {string} mode - 'session' | 'persistent' | 'all'
 */
export function clearTableState(tableName, contextKey, mode = 'all') {
  if (!tableName) return

  const key = getStorageKey(tableName, contextKey)

  try {
    if (mode === 'all' || mode === 'session') {
      sessionStorage.removeItem(key)
    }
    if (mode === 'all' || mode === 'persistent') {
      localStorage.removeItem(key)
    }
  } catch (e) {
    // Ignore errors
  }
}

export default {
  saveTableState,
  loadTableState,
  clearTableState
}
